import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, FileText, Star, Award, Instagram, Music, TrendingUp, User, Sparkles, Facebook, Palette, Scissors } from "lucide-react";
import InputField from "../../../shared/components/form/InputField";
import SelectField from "../../../shared/components/form/SelectField";
import MultiSelectField from "../../../shared/components/form/MultiSelectField";
import NationalitySelect from "../../../shared/components/NationalitySelect";
import LanguageDialectSelect from "../../../shared/components/LanguageDialectSelect";
import QuestionsSection from "./QuestionsSection";
import type { TalentFormData, TalentFormErrors } from "../types/register-talent.types";
import { EXPERIENCE_LEVELS, TALENT_CATEGORIES, SKIN_TONE_OPTIONS, HAIR_TYPE_OPTIONS } from "../utils/register-talent.utils";
import { EYE_COLOR_OPTIONS, HAIR_COLOR_OPTIONS, BODY_TYPE_OPTIONS, LOOK_ALIKE_OPTIONS } from "../../../shared/constants/talentOptions";
import type { Question } from "../../admin/hooks/useQuestionsManagement";
import TextAreaField from "../../../shared/components/form/TextAreaField";
import { useLanguage } from "../../../context/LanguageContext";

interface Props {
  formData: TalentFormData;
  formErrors: TalentFormErrors;
  liveErrors: TalentFormErrors;
  computedAge: number | null;
  getAgeCategory: (age: number) => string;
  specialtiesOptions: { id: number; name: string; options?: { id: number; name: string }[] }[];
  isLoading: boolean;
  questions: Question[];
  questionAnswers: Record<number, boolean>;
  onAnswer: (id: number, value: boolean) => void;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
  onDateOfBirthChange: (value: string) => void;
  onSocialMediaChange: (platform: keyof TalentFormData["socialMedia"], value: string) => void;
  onMeasurementsChange: (field: keyof TalentFormData["measurements"], value: string) => void;
  onCategoryGroupChange: (category: string, values: string[]) => void;
  onSpecialtiesChange: (ids: string[]) => void;
  onSpecialtyOptionsChange: (ids: string[]) => void;
  onLanguagesChange: (values: string[]) => void;
  onDialectsChange: (values: string[]) => void;
  onCustomLanguageChange: (value: string) => void;
  onBack: () => void;
  onSubmit: (e: React.FormEvent) => void;
  talentType?: string;
  onSpecializationsChange?: (values: string[]) => void;
}

export default function TalentDetailsStep({
  formData, liveErrors, computedAge, getAgeCategory, specialtiesOptions, isLoading,
  questions, questionAnswers, onAnswer,
  onChange, onDateOfBirthChange, onSocialMediaChange, onMeasurementsChange,
  onCategoryGroupChange, onSpecialtiesChange, onSpecialtyOptionsChange,
  onLanguagesChange, onDialectsChange, onCustomLanguageChange,
  onBack, onSubmit,
}: Props) {
  const { t } = useLanguage();
  // Only show errors after the user has attempted to submit
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const allAnswered = questions.length === 0 || questions.every((q) => q.id in questionAnswers);
  const hasErrors = Object.keys(liveErrors).length > 0 || !allAnswered;
  const todayStr = new Date().toISOString().split("T")[0];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (hasErrors) {
      // Scroll to first error
      const first = formRef.current?.querySelector("[data-error]") as HTMLElement | null;
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    onSubmit(e);
  };

  const err = (field: keyof TalentFormErrors) => submitted ? liveErrors[field] : undefined;

  return (
    <motion.form ref={formRef} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6" onSubmit={handleSubmit}>
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-2">{t("auth.talentDetailsStep.title")}</h3>
        <p className="text-sm text-gray-500">{t("auth.talentDetailsStep.subtitle")}</p>
      </div>

      {/* Summary banner — only after submit attempt */}
      {submitted && hasErrors && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          <p className="font-semibold mb-1">{t("auth.talentDetailsStep.fixHighlighted")}</p>
          <ul className="list-disc list-inside space-y-0.5">
            {Object.values(liveErrors).map((msg, i) => <li key={i}>{msg}</li>)}
            {!allAnswered && <li>{t("auth.talentDetailsStep.answerAllQuestions")}</li>}
          </ul>
        </div>
      )}

      {/* Personal */}
      <div className="grid md:grid-cols-2 gap-5">
        <div data-error={err("date_of_birth") ? true : undefined}>
          <InputField
            label={t("auth.talentDetailsStep.dateOfBirth")} icon={Calendar} type="date" name="date_of_birth"
            value={formData.date_of_birth} max={todayStr}
            onChange={(e) => onDateOfBirthChange(e.target.value)}
            error={err("date_of_birth")}
          />
        </div>
        <InputField label={t("auth.talentDetailsStep.age")} icon={User} type="text"
          value={computedAge !== null ? t("auth.talentDetailsStep.ageYears", { age: computedAge }) : ""} readOnly placeholder={t("auth.talentDetailsStep.ageCalculatedAutomatically")} />
        <div data-error={err("nationality") ? true : undefined}>
          <NationalitySelect name="nationality" value={formData.nationality} onChange={onChange}
            error={err("nationality")} required />
        </div>
        <InputField label={t("auth.talentDetailsStep.city")} icon={MapPin} name="city" value={formData.city} onChange={onChange} placeholder={t("auth.talentDetailsStep.cityPlaceholder")} />
      </div>

      {computedAge !== null && (
        <p className="text-sm text-gray-500">{t("auth.talentDetailsStep.ageCategory")} <span className="font-semibold text-gray-700">{t(getAgeCategory(computedAge))}</span></p>
      )}

      {/* Specialties */}
      <div data-error={err("specialties") ? true : undefined}>
        <MultiSelectField
          label={t("auth.talentDetailsStep.specialties")} icon={Star}
          options={specialtiesOptions.map((s) => ({ value: String(s.id), label: s.name }))}
          selected={formData.specialties}
          onChange={onSpecialtiesChange}
          error={err("specialties")}
        />
      </div>

      {/* Sub-options for any selected specialty that has them (e.g. Dancer → dance styles, Sport → sports) */}
      {specialtiesOptions
        .filter((s) => formData.specialties.includes(String(s.id)) && (s.options?.length ?? 0) > 0)
        .map((s) => {
          const subOptionIds = s.options!.map((o) => o.id);
          return (
            <div key={s.id} className="ml-4 pl-4 border-l-2 border-[#b3d130]/40">
              <MultiSelectField
                label={`${s.name} — ${t("auth.talentDetailsStep.specialization")}`}
                icon={Star}
                options={s.options!.map((o) => ({ value: o.id, label: o.name }))}
                selected={formData.specialty_options.filter((id) => subOptionIds.includes(id))}
                onChange={(selected: number[]) => {
                  const otherSelections = formData.specialty_options.filter(
                    (id) => !subOptionIds.includes(id),
                  );
                  onSpecialtyOptionsChange([...otherSelections, ...selected] as unknown as string[]);
                }}
              />
            </div>
          );
        })}

      {/* Gender + Special Talents */}
      <div className="space-y-3">
        <div className="text-sm font-semibold text-gray-700">{t("auth.talentDetailsStep.talentCategories")}</div>
        {TALENT_CATEGORIES.map((cat) => {
          const selectedValues = formData.categories.filter((o) =>
            cat.options.some((opt) => typeof opt === "object" ? opt.value === o : opt === o)
          );
          return (
            <div key={cat.category} data-error={cat.category === "Gender" && err("categories") ? true : undefined}>
              <MultiSelectField
                label={cat.category === "Gender" ? t("auth.talentDetailsStep.gender") : cat.category}
                icon={Star}
                options={cat.category === "Gender"
                  ? [
                      { value: "Male", label: t("auth.validation.genderMale") },
                      { value: "Female", label: t("auth.validation.genderFemale") },
                    ]
                  : cat.options}
                selected={selectedValues}
                onChange={(values) => onCategoryGroupChange(cat.category, values)}
                error={cat.category === "Gender" ? err("categories") : undefined}
              />
            </div>
          );
        })}
      </div>

      {/* Skin Tone — single select */}
      <SelectField
        label={t("auth.talentDetailsStep.skinTone")} icon={Palette}
        name="skin_tone" value={formData.skin_tone}
        onChange={onChange}
        options={SKIN_TONE_OPTIONS}
      />

      {/* Hair Type — single select */}
      <SelectField
        label={t("auth.talentDetailsStep.hairType")} icon={Scissors}
        name="hair_type" value={formData.hair_type}
        onChange={onChange}
        options={HAIR_TYPE_OPTIONS}
      />

      {/* Eye Color */}
      <SelectField
        label={t("auth.talentDetailsStep.eyeColor")} icon={Palette}
        name="eye_color" value={formData.eye_color}
        onChange={onChange}
        options={EYE_COLOR_OPTIONS}
      />

      {/* Hair Color */}
      <SelectField
        label={t("auth.talentDetailsStep.hairColor")} icon={Scissors}
        name="hair_color" value={formData.hair_color}
        onChange={onChange}
        options={HAIR_COLOR_OPTIONS}
      />

      {/* Body Type */}
      <SelectField
        label={t("auth.talentDetailsStep.bodyType")} icon={User}
        name="body_type" value={formData.body_type}
        onChange={onChange}
        options={BODY_TYPE_OPTIONS}
      />

      {/* Languages + Dialects */}
      <div data-error={err("languages") ? true : undefined}>
        <LanguageDialectSelect
          languages={formData.languages}
          dialects={formData.dialects}
          onLanguagesChange={onLanguagesChange}
          onDialectsChange={onDialectsChange}
          customLanguage={formData.custom_language}
          onCustomLanguageChange={onCustomLanguageChange}
          error={err("languages")}
        />
      </div>

      {/* Look Alike */}
      <SelectField
        label={t("auth.talentDetailsStep.lookAlike")} icon={Sparkles}
        name="look_alike" value={formData.look_alike}
        onChange={onChange}
        options={LOOK_ALIKE_OPTIONS}
      />

      <SelectField label={t("auth.talentDetailsStep.experienceLevel")} icon={Award} required name="experience_years"
        value={formData.experience_years} onChange={onChange}
        error={err("experience_years")} options={EXPERIENCE_LEVELS} />

      <TextAreaField label={t("auth.talentDetailsStep.bio")} icon={FileText} name="bio"
        value={formData.bio} onChange={onChange} placeholder={t("auth.talentDetailsStep.bioPlaceholder")} />

      {/* Social Media */}
      <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
        <h4 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <TrendingUp size={18} className="text-[#b3d130]" /> {t("auth.talentDetailsStep.socialMediaProfiles")}
        </h4>
        <div className="grid md:grid-cols-3 gap-4">
          <InputField label="Instagram" icon={Instagram} name="instagram" value={formData.socialMedia?.instagram || ""} onChange={(e) => onSocialMediaChange("instagram", e.target.value)} placeholder="@username" />
          <InputField label="Facebook" icon={Facebook} name="facebook" value={formData.socialMedia?.facebook || ""} onChange={(e) => onSocialMediaChange("facebook", e.target.value)} placeholder="@username" />
          <InputField label="TikTok" icon={Music} name="tiktok" value={formData.socialMedia?.tiktok || ""} onChange={(e) => onSocialMediaChange("tiktok", e.target.value)} placeholder="@username" />
        </div>
      </div>

      {/* Physical Details */}
      <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
        <h4 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <User size={18} className="text-[#b3d130]" /> {t("auth.talentDetailsStep.physicalDetails")}
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {([["height", "auth.talentDetailsStep.heightCm", "cm"], ["weight", "auth.talentDetailsStep.weightKg", "kg"], ["tshirtSize", "auth.talentDetailsStep.tshirt", "M"], ["pant", "auth.talentDetailsStep.pant", "32"], ["shoeSize", "auth.talentDetailsStep.shoe", "42"]] as const).map(([field, labelKey, placeholder]) => (
            <InputField key={field} label={t(labelKey)} icon={User} name={field}
              value={formData.measurements?.[field] || ""}
              onChange={(e) => onMeasurementsChange(field, e.target.value)}
              placeholder={placeholder}
            />
          ))}
        </div>
      </div>

      {/* Rates */}
      <div className="bg-gray-50 rounded-2xl p-6">
        <h4 className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-4">
          <Sparkles size={18} className="text-[#b3d130]" /> {t("auth.talentDetailsStep.ratesPricing")}
        </h4>
        <InputField label={t("auth.talentDetailsStep.ratesOptional")} icon={Sparkles} name="rates" value={formData.rates} onChange={onChange} placeholder={t("auth.talentDetailsStep.ratesPlaceholder")} />
      </div>

      {/* Admin Questions */}
      <QuestionsSection
        questions={questions}
        questionAnswers={questionAnswers}
        isLoading={isLoading}
        onAnswer={onAnswer}
      />

      {/* Actions */}
      <div className="flex gap-4 pt-2">
        <button type="button" onClick={onBack}
          className="flex-1 bg-white border-2 border-gray-300 text-gray-700 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all">
          {t("auth.back")}
        </button>
        <button type="submit"
          className="flex-1 bg-gradient-to-r from-[#b3d130] to-[#a5c428] text-gray-900 py-4 rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-[1.02]">
          {t("auth.talentDetailsStep.continueToGallery")}
        </button>
      </div>
    </motion.form>
  );
}
